import { defineStore, getActivePinia } from "pinia";
import router from "../router/router.js";
import axios from "axios";
import {
  getToken,
  setToken,
  ecomLogin,
  customerLogin,
  logout,
  registerEcom,
} from "rdep-ecom-sdk";
import { useAppStore } from "./useAppStore.js";
import { useCartStore } from "./useCartStore.js";

const disconnect = () => {};

const CURRENT_USER = "auth.currentUser";
const REGISTER_RESPONSE_KEY = "register.api.response";
const CART_ID_STORAGE_KEY = "cart_id";
const CART_META_STORAGE_KEY = "ecom.cart.meta";
const AUTH_CHANNEL_NAME = "auth";
const AUTH_LOGOUT_EVENT = "LOGOUT";
const LOGOUT_TOAST_MESSAGE_KEY = "navbar.logout.toast.message";
const TOKEN_EXPIRED_TOAST_MESSAGE = "Token is expired login to continue.";
const initialState = getSavedState(CURRENT_USER);
const authChannel = createAuthBroadcastChannel();

setDefaultAuthHeaders(initialState);

export const useAuthStore = defineStore("auth", {
  state: () => ({
    auth: initialState,
    loginResponse: null,
    logoutResponse: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.auth,
  },

  actions: {
    getUser() {
      return getSavedState(CURRENT_USER);
    },

    async LogIn(authDetails) {
      const appStore = useAppStore();
      if (!appStore.baseUrl) {
        appStore.initializeDomainConfig();
      }

      const domainName = appStore.domainName;
      const normalizedRole = String(
        authDetails?.role || authDetails?.loginRole || "",
      )
        .trim()
        .toLowerCase();
      const selectedRole =
        normalizedRole === "customer" ? "customer" : "distributor";
      const loginApi = selectedRole === "customer" ? customerLogin : ecomLogin;

      try {
        const response = await loginApi({
          username: authDetails.username,
          password: authDetails.password,
          domainName,
        });

        this.loginResponse = response || null;

        if (response?.statusCode === "200" || !!getToken()) {
          const token = getToken() || "";
          this.auth = {
            ...response,
            token,
            role: selectedRole,
          };

          if (!this.auth.landingPage) {
            this.auth.landingPage = "/";
          }

          saveState(CURRENT_USER, this.auth);
          setDefaultAuthHeaders(this.auth);

          // If user added items as guest, push them to server cart on login.
          const cartStore = useCartStore();
          await cartStore.syncGuestCartAfterLogin();

          return response;
        }

        if (response?.statusCode === "400" && response?.twoFaEnabled) {
          return response;
        }

        throw new Error(response?.statusMessage || "Login failed");
      } catch (error) {
        throw new Error(
          error?.response?.data?.statusMessage ||
            error.message ||
            "Login failed",
        );
      }
    },

    async loginAfterRegister(registrationDetails) {
      const appStore = useAppStore();
      if (!appStore.baseUrl) {
        appStore.initializeDomainConfig();
      }

      const domainName = appStore.domainName;

      try {
        const response = await registerEcom({
          ...registrationDetails,
          domainName,
        });

        // Save latest register API response globally for downstream pages.
        window.localStorage.setItem(
          REGISTER_RESPONSE_KEY,
          JSON.stringify(response?.data ?? response ?? {}),
        );

        const loginResponse =
          response?.loginResponse || response?.data?.loginResponse || response;
        const statusCode = String(
          loginResponse?.statusCode ?? response?.statusCode ?? "",
        );
        const payloadToken = extractAuthToken(loginResponse);
        const sdkToken = String(getToken() || "").trim();
        const resolvedToken = payloadToken || sdkToken;

        if (payloadToken) {
          // Prefer login payload token for register flow; API top-level token can
          // be non-cart issuer in some environments.
          setToken(payloadToken);
        }

        this.loginResponse = loginResponse || null;

        if (statusCode === "200" || !!resolvedToken) {
          this.auth = {
            ...loginResponse,
            token: resolvedToken,
          };

          // if (!this.auth.landingPage) {
          //   this.auth.landingPage = "/";
          // }

          saveState(CURRENT_USER, this.auth);
          setDefaultAuthHeaders(this.auth);

          // If user added items as guest, push them to server cart on login.
          const cartStore = useCartStore();
          await cartStore.syncGuestCartAfterLogin();

          return response;
        }

        if (statusCode === "400" && loginResponse?.twoFaEnabled) {
          return response;
        }

        throw new Error(
          loginResponse?.statusMessage ||
            response?.statusMessage ||
            "Login failed",
        );
      } catch (error) {
        throw new Error(
          error?.response?.data?.statusMessage ||
            error.message ||
            "Login failed",
        );
      }
    },

    logoutOntokenExpired() {
      const toastMessage = TOKEN_EXPIRED_TOAST_MESSAGE;
      this.clearAuthState({
        redirectPath: "/login",
        toastMessage,
      });
      broadcastLogout({
        redirectPath: "/login",
        toastMessage,
      });
    },

    async logOut() {
      const token = this.auth?.token;
      disconnect(token);

      try {
        await logout();
        this.logoutResponse = {
          statusCode: "200",
          status: "success",
          statusMessage:
            "User is logged out, please log in again to continue shopping",
        };
      } catch (error) {
        this.logoutResponse = {
          statusCode: "500",
          status: "error",
          statusMessage:
            error?.response?.data?.statusMessage ||
            error.message ||
            "Logout failed",
        };
      }

      this.clearAuthState();

      broadcastLogout();
      sessionStorage.removeItem("julien");
      localStorage.removeItem("modalActive");
      return this.logoutResponse;
    },

    // resetPassword({ username, tenantSubDomain } = {}) {
    //   return axios
    //     .post("/auth-service/noauth/password/setNewPassword/generateOtp", {
    //       username,
    //       tenantSubDomain,
    //     })
    //     .then((response) => response.data);
    // },

    clearAuthState({ redirectPath = "/", toastMessage = "" } = {}) {
      setToken("");
      setDefaultAuthHeaders(null);
      this.auth = null;
      saveState(CURRENT_USER, null);
      window.localStorage.removeItem(CART_ID_STORAGE_KEY);
      window.localStorage.removeItem(CART_META_STORAGE_KEY);
      if (toastMessage) {
        window.sessionStorage.setItem(LOGOUT_TOAST_MESSAGE_KEY, toastMessage);
      }
      const cartStore = useCartStore();
      cartStore.$reset();
      window.history.replaceState({}, "", redirectPath);
      router.replace(redirectPath).catch(() => {});
    },

    syncAuthTokenFromSource(source) {
      const nextToken = extractAuthToken(source);
      if (!nextToken) {
        return "";
      }

      setToken(nextToken);

      const currentAuth = this.auth || getSavedState(CURRENT_USER) || {};
      const updatedAuth = {
        ...currentAuth,
        token: nextToken,
      };

      this.auth = updatedAuth;
      saveState(CURRENT_USER, updatedAuth);
      setDefaultAuthHeaders(updatedAuth);

      return nextToken;
    },
  },
});

function setDefaultAuthHeaders(currentUser) {
  const token = currentUser?.token || "";
  let authorizationHeader = "";
  if (token) {
    authorizationHeader = token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`;
  }

  axios.defaults.headers.common.Authorization = authorizationHeader;
}

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state));
}

function getSavedState(key) {
  return JSON.parse(window.localStorage.getItem(key)) || null;
}

function extractAuthToken(payload) {
  if (!payload || typeof payload !== "object") {
    return "";
  }

  const nested = payload?.data || {};

  const token =
    payload.token ||
    payload.accessToken ||
    payload.access_token ||
    payload.authToken ||
    payload.authorization ||
    payload.Authorization ||
    payload.bearerToken ||
    nested.token ||
    nested.accessToken ||
    nested.access_token ||
    nested.authToken ||
    nested.authorization ||
    nested.Authorization ||
    nested.bearerToken ||
    payload.jwt ||
    nested.jwt ||
    "";

  return String(token || "").trim();
}

if (authChannel) {
  authChannel.onmessage = (event) => {
    if (event?.data?.type !== AUTH_LOGOUT_EVENT) {
      return;
    }

    applyCrossTabLogout();
  };
}

function createAuthBroadcastChannel() {
  if (typeof window === "undefined" || !window.BroadcastChannel) {
    return null;
  }

  return new BroadcastChannel(AUTH_CHANNEL_NAME);
}

function broadcastLogout() {
  if (!authChannel) {
    return;
  }

  authChannel.postMessage({
    type: AUTH_LOGOUT_EVENT,
    redirectPath: "/",
  });
}

function applyCrossTabLogout(eventData = {}) {
  const redirectPath = String(eventData?.redirectPath || "/").trim() || "/";
  const toastMessage = String(eventData?.toastMessage || "").trim();

  setToken("");
  setDefaultAuthHeaders(null);
  saveState(CURRENT_USER, null);
  window.localStorage.removeItem(CART_ID_STORAGE_KEY);
  window.localStorage.removeItem(CART_META_STORAGE_KEY);
  if (toastMessage) {
    window.sessionStorage.setItem(LOGOUT_TOAST_MESSAGE_KEY, toastMessage);
  }
  sessionStorage.removeItem("julien");
  localStorage.removeItem("modalActive");

  const pinia = getActivePinia();
  if (pinia) {
    const authStore = useAuthStore(pinia);
    authStore.auth = null;
    authStore.loginResponse = null;
  }

  window.history.replaceState({}, "", redirectPath);
  router.replace(redirectPath).catch(() => {});
}
